import {
  useContext,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type MouseEvent,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCalendarCheck,
  FaChevronDown,
  FaMagnifyingGlass,
  FaTriangleExclamation,
  FaUserPlus,
  FaUsersSlash,
  FaXmark,
} from "react-icons/fa6";
import http from "../api/apiClient";
import { AuthContext } from "../AuthProvider";
import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import Badge from "../components/Badge";
import SchoolYearDropdown from "../components/SchoolYearDropdown";
import { useTranslation } from "react-i18next";
import GenderIcon from "../components/GenderIcon";
import CommittedValidate from "../components/CommitedValidate";
import WaitingValidate from "../components/WaitingValidate";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import GetSchoolYear from "../utils/GetSchoolYear";
import { assignChildToClassroom } from "../utils/assignChildToClassroom";
import { calculateAge, parseToDate } from "../utils/dateUtils";
import { suggestClassroomForChild } from "../utils/suggestClassroom";
import { translateClassName } from "../utils/classroomUtils";
import { useCan } from "../authz/useCan";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";

type Identifier = string | number;

interface SchoolYear {
  id?: Identifier;
  startDate?: unknown;
  endDate?: unknown;
}

interface Classroom {
  id: Identifier;
  className: string;
  startDate?: unknown;
  endDate?: unknown;
  grade?: string | number | null;
  classType?: string | null;
  level?: string | null;
  schoolYear?: SchoolYear;
}

interface ClassroomAssignment {
  id?: Identifier;
  className?: string;
  classroomName?: string;
  startDate?: unknown;
  endDate?: unknown;
  current?: boolean;
  isCurrent?: boolean;
  schoolYearLabel?: string;
  schoolYear?: SchoolYear;
  classroom?: {
    className?: string;
    startDate?: unknown;
    endDate?: unknown;
  };
}

interface Child {
  id: Identifier;
  firstName?: string;
  lastName?: string;
  gender?: string;
  committed?: boolean;
  waiting?: boolean;
  dateBirth?: string | Date | null;
  deceasedAt?: unknown;
  withdrawnAt?: unknown;
  classrooms?: ClassroomAssignment[];
  futureClassroomAssignments?: ClassroomAssignment[];
}

interface CategorizedChildren {
  actionRequired: Child[];
  mayAttend: Child[];
  futurePlanned: Child[];
  unassigned: Child[];
}

type ChildSortKey = "name" | "birthDate" | "projectedYear" | "classroom";
type SortDirection = "ascending" | "descending";

interface HttpMappedError {
  message?: string;
  status?: number;
}

function getAssignmentStartDate(
  assignment: ClassroomAssignment
): Date | null {
  return parseToDate(
    assignment.startDate ??
      assignment.classroom?.startDate ??
      assignment.schoolYear?.startDate
  );
}

function getAssignmentEndDate(
  assignment: ClassroomAssignment
): Date | null {
  return parseToDate(
    assignment.endDate ??
      assignment.classroom?.endDate ??
      assignment.schoolYear?.endDate
  );
}

function isCurrentAssignment(
  assignment: ClassroomAssignment
): boolean {
  return (
    assignment.current === true ||
    assignment.isCurrent === true
  );
}

function getFutureClassroomAssignments(
  child: Child,
  today: Date
): ClassroomAssignment[] {
  const startOfToday = new Date(today);
  startOfToday.setHours(0, 0, 0, 0);

  return (Array.isArray(child.classrooms) ? child.classrooms : [])
    .filter((assignment) => {
      const startDate = getAssignmentStartDate(assignment);

      return (
        !isCurrentAssignment(assignment) &&
        Boolean(startDate && startDate > startOfToday)
      );
    })
    .sort((left, right) => {
      const leftDate = getAssignmentStartDate(left)?.getTime() ?? 0;
      const rightDate = getAssignmentStartDate(right)?.getTime() ?? 0;
      return leftDate - rightDate;
    });
}

function toArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function getYear(value: unknown): number | null {
  const date = parseToDate(value);
  return date ? date.getFullYear() : null;
}

const classroomNameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

function compareAssignmentClassrooms(left: Classroom, right: Classroom): number {
  const rank = (classroom: Classroom) => {
    const type = String(classroom.classType ?? classroom.level ?? "").toUpperCase();
    if (type === "KINDERGARTEN") return 0;
    if (type === "GRADE") return 1;
    return 2;
  };

  const rankDifference = rank(left) - rank(right);
  if (rankDifference !== 0) return rankDifference;

  const leftGrade = Number(left.grade);
  const rightGrade = Number(right.grade);
  if (Number.isFinite(leftGrade) && Number.isFinite(rightGrade) && leftGrade !== rightGrade) {
    return leftGrade - rightGrade;
  }

  return classroomNameCollator.compare(left.className, right.className);
}

export default function ChildrenUnassigned(): ReactElement {
  const { keycloak } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canManageWaiting = useCan(
    "school.waiting-list",
    "assign"
  );

  const [children, setChildren] = useState<Child[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [assignedChildren, setAssignedChildren] = useState<
    Identifier[]
  >([]);
  const [selectedChild, setSelectedChild] =
    useState<Child | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] =
    useState<Identifier | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortKey, setSortKey] = useState<ChildSortKey>("birthDate");
  const [sortDirection, setSortDirection] =
    useState<SortDirection>("ascending");
  const [classroomSearchTerm, setClassroomSearchTerm] =
    useState<string>("");
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [modalSchoolYearId, setModalSchoolYearId] = useState<string>("");
  const [isMobileLayout, setIsMobileLayout] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [error, setError] =
    useState<HttpMappedError | null>(null);

  const today = useMemo(() => new Date(), []);

  const currentYear = schoolYears.find((schoolYear) => {
    const start = parseToDate(schoolYear.startDate);
    const end = parseToDate(schoolYear.endDate);

    return Boolean(
      start && end && start <= today && end >= today
    );
  });

  const selectedModalYear = schoolYears.find(
    (schoolYear) => String(schoolYear.id) === modalSchoolYearId
  ) ?? currentYear;

  const modalClassrooms = useMemo(() => {
    if (!selectedModalYear) return [];

    const selectedStartYear = getYear(selectedModalYear.startDate);
    const selectedEndYear = getYear(selectedModalYear.endDate);

    return classrooms.filter((classroom) => {
      if (classroom.schoolYear?.id != null && selectedModalYear.id != null) {
        return String(classroom.schoolYear.id) === String(selectedModalYear.id);
      }

      return getYear(classroom.startDate) === selectedStartYear
        && getYear(classroom.endDate) === selectedEndYear;
    }).sort(compareAssignmentClassrooms);
  }, [classrooms, selectedModalYear]);

  useEffect(() => {
    if (!showModal) return;
    setModalSchoolYearId(currentYear?.id != null ? String(currentYear.id) : "");
  }, [currentYear?.id, showModal]);

  useEffect(() => {
    if (showDropdown === null) {
      return;
    }

    const handleOutsideClick = (event: globalThis.MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      const element = target instanceof Element ? target : target.parentElement;
      if (element?.closest(`[data-cu-dropdown="${String(showDropdown)}"]`)) {
        return;
      }

      setShowDropdown(null);
      setDropdownPosition(null);
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () =>
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
  }, [showDropdown]);

  useEffect(() => {
    const updateLayout = (): void => {
      setIsMobileLayout(window.innerWidth < 1024);
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    window.addEventListener("orientationchange", updateLayout);

    return () => {
      window.removeEventListener("resize", updateLayout);
      window.removeEventListener(
        "orientationchange",
        updateLayout
      );
    };
  }, []);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const [childrenResponse, classroomsResponse, yearsResponse] =
          await Promise.all([
            http.get("/reports/unassigned-children"),
            http.get("/classrooms"),
            http.get("/years"),
          ]);

        setSchoolYears(
          toArray<SchoolYear>(yearsResponse.data)
        );

        const activeChildren = toArray<Child>(
          childrenResponse.data
        ).filter(
          (child) =>
            !child.deceasedAt && !child.withdrawnAt
        );

        setChildren(activeChildren);
        setClassrooms(
          toArray<Classroom>(classroomsResponse.data)
        );
      } catch (caughtError) {
        setError(
          mapHttpErrorToMessage(
            caughtError,
            t
          ) as HttpMappedError
        );
      } finally {
        setLoading(false);
      }
    };

    if (keycloak?.token) {
      void fetchData();
    }
  }, [keycloak?.token, t]);

  const formatDate = (value: unknown): string => {
    const date = parseToDate(value);
    return date
      ? date.toLocaleDateString("nl-NL")
      : "—";
  };

  const formatSchoolYearRange = (
    startValue: unknown,
    endValue: unknown
  ): string => {
    const startYear = getYear(startValue);
    const endYear = getYear(endValue);

    return startYear && endYear
      ? `${startYear} – ${endYear}`
      : "—";
  };

  const getFutureAssignmentLabel = (
    assignment: ClassroomAssignment
  ): string => {
    const classroomName =
      translateClassName(assignment) ||
      assignment.classroomName ||
      assignment.className ||
      assignment.classroom?.className ||
      "—";

    const schoolYear =
      assignment.schoolYearLabel ||
      formatSchoolYearRange(
        getAssignmentStartDate(assignment),
        getAssignmentEndDate(assignment)
      );

    return `${classroomName} · ${schoolYear}`;
  };

  const childrenWithFutureAssignments = useMemo<Child[]>(
    () =>
      children.map((child) => ({
        ...child,
        futureClassroomAssignments:
          getFutureClassroomAssignments(child, today),
      })),
    [children, today]
  );

  const searchFilteredChildren = useMemo<Child[]>(() => {
    const normalizedSearch = searchTerm
      .trim()
      .toLowerCase();

    const filtered = normalizedSearch
      ? childrenWithFutureAssignments.filter((child) =>
          `${child.firstName ?? ""} ${child.lastName ?? ""}`
            .toLowerCase()
            .includes(normalizedSearch)
        )
      : childrenWithFutureAssignments;

    const direction = sortDirection === "ascending" ? 1 : -1;
    return [...filtered].sort((left, right) => {
      if (sortKey === "birthDate") {
        const leftBirth = parseToDate(left.dateBirth)?.getTime();
        const rightBirth = parseToDate(right.dateBirth)?.getTime();
        if (leftBirth == null) return rightBirth == null ? 0 : 1;
        if (rightBirth == null) return -1;
        return direction * (leftBirth - rightBirth);
      }

      const getSortValue = (child: Child): string => {
        if (sortKey === "name") {
          return `${child.firstName ?? ""} ${child.lastName ?? ""}`.trim();
        }

        const schoolYearInfo =
          GetSchoolYear(child.dateBirth, classrooms, schoolYears) || {};
        if (sortKey === "projectedYear") {
          return String(
            (schoolYearInfo as { projectedYear?: unknown }).projectedYear ?? ""
          );
        }

        return String(
          suggestClassroomForChild(child, classrooms, currentYear) ?? ""
        );
      };

      return direction * getSortValue(left).localeCompare(
        getSortValue(right),
        undefined,
        { numeric: true, sensitivity: "base" }
      );
    });
  }, [
    childrenWithFutureAssignments,
    classrooms,
    currentYear,
    schoolYears,
    searchTerm,
    sortDirection,
    sortKey,
  ]);

  const handleSort = (key: ChildSortKey): void => {
    if (sortKey === key) {
      setSortDirection((current) =>
        current === "ascending" ? "descending" : "ascending"
      );
      return;
    }

    setSortKey(key);
    setSortDirection("ascending");
  };

  const sortIndicator = (key: ChildSortKey): string => {
    if (sortKey !== key) return "↕";
    return sortDirection === "ascending" ? "↑" : "↓";
  };

  const categorizedChildren =
    useMemo<CategorizedChildren>(() => {
      const categories: CategorizedChildren = {
        actionRequired: [],
        mayAttend: [],
        futurePlanned: [],
        unassigned: [],
      };

      searchFilteredChildren.forEach((child) => {
        const age = calculateAge(child.dateBirth, today);
        const hasFutureAssignments =
          (child.futureClassroomAssignments?.length ?? 0) > 0;

        if (age >= 5) {
          categories.actionRequired.push(child);
          return;
        }

        if (age >= 4) {
          categories.mayAttend.push(child);
          return;
        }

        if (hasFutureAssignments) {
          categories.futurePlanned.push(child);
          return;
        }

        categories.unassigned.push(child);
      });

      return categories;
    }, [searchFilteredChildren, today]);

  const handleAssign = async (
    child: Child,
    classroom: Classroom
  ): Promise<void> => {
    if (assignedChildren.includes(child.id)) {
      return;
    }

    await assignChildToClassroom(
      null,
      child.id,
      classroom.id,
      {
        grade: classroom.grade ?? null,
        level:
          classroom.classType ||
          classroom.level ||
          null,
        setCurrent: true,
      },
      setChildren
    );

    setAssignedChildren((current) => [
      ...current,
      child.id,
    ]);
    setShowModal(false);
    setSelectedChild(null);
    setClassroomSearchTerm("");
  };

  const handleMobileBack = (): void => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const renderSectionHeader = (
    title: string,
    description: string,
    count: number,
    tone: "danger" | "warning" | "success" | "neutral"
  ): ReactElement => {
    const toneClasses = {
      danger:
        "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300",
      warning:
        "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300",
      success:
        "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300",
      neutral:
        "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300",
    };

    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-950 dark:text-white">
            {title}
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {description}
          </p>
        </div>

        <span
          className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-bold ${toneClasses[tone]}`}
        >
          {count}
        </span>
      </div>
    );
  };

  const renderChildrenTable = (
    items: Child[],
    options: {
      showFutureAssignments?: boolean;
    } = {}
  ): ReactElement => {
    const showFutureAssignments =
      options.showFutureAssignments ?? false;
    const allowAssignmentActions =
      canManageWaiting && !showFutureAssignments;

    return (
      <LdsTableCard className="mt-4">
        <LdsTableScroll className="overflow-y-visible">
          <LdsTable>
            <LdsTableHead>
              <tr>
                <LdsTableHeadCell>
                  {t("children.table.gender", {
                    defaultValue: "Gender",
                  })}
                </LdsTableHeadCell>

                <LdsTableHeadCell
                  aria-sort={sortKey === "name" ? sortDirection : "none"}
                >
                  <button
                    type="button"
                    onClick={() => handleSort("name")}
                    className="inline-flex items-center gap-2"
                  >
                    {t("children.table.name", {
                      defaultValue: "Name",
                    })}
                    <span aria-hidden="true">{sortIndicator("name")}</span>
                  </button>
                </LdsTableHeadCell>

                <LdsTableHeadCell
                  aria-sort={sortKey === "birthDate" ? sortDirection : "none"}
                >
                  <button
                    type="button"
                    onClick={() => handleSort("birthDate")}
                    className="inline-flex items-center gap-2"
                  >
                    {t("children.table.birth_date", {
                      defaultValue: "Date of birth",
                    })}
                    <span aria-hidden="true">{sortIndicator("birthDate")}</span>
                  </button>
                </LdsTableHeadCell>

                <LdsTableHeadCell
                  aria-sort={sortKey === "projectedYear" ? sortDirection : "none"}
                >
                  <button
                    type="button"
                    onClick={() => handleSort("projectedYear")}
                    className="inline-flex items-center gap-2"
                  >
                    {t("waiting-list.projected_year", {
                      defaultValue: "Projected school year",
                    })}
                    <span aria-hidden="true">{sortIndicator("projectedYear")}</span>
                  </button>
                </LdsTableHeadCell>

                <LdsTableHeadCell
                  aria-sort={sortKey === "classroom" ? sortDirection : "none"}
                >
                  <button
                    type="button"
                    onClick={() => handleSort("classroom")}
                    className="inline-flex items-center gap-2"
                  >
                    {showFutureAssignments
                      ? t(
                          "reports.unassigned.future_classroom",
                          { defaultValue: "Future classroom" }
                        )
                      : t(
                          "waiting-list.suggested_class",
                          { defaultValue: "Suggested classroom" }
                        )}
                    <span aria-hidden="true">{sortIndicator("classroom")}</span>
                  </button>
                </LdsTableHeadCell>

                {allowAssignmentActions ? (
                  <LdsTableHeadCell className="text-right">
                    {t("children.table.actions", {
                      defaultValue: "Actions",
                    })}
                  </LdsTableHeadCell>
                ) : null}
              </tr>
            </LdsTableHead>

            <LdsTableBody className="[&>tr]:animate-[lds-section-enter_360ms_cubic-bezier(0.22,1,0.36,1)_both]">
              {items.map((child) => {
                const suggestedClassName =
                  suggestClassroomForChild(
                    child,
                    classrooms,
                    currentYear
                  );

                const isTooYoung =
                  suggestedClassName === "Too young";

                const startYear = currentYear
                  ? getYear(currentYear.startDate)
                  : null;
                const endYear = currentYear
                  ? getYear(currentYear.endDate)
                  : null;

                const currentSchoolYearLabel =
                  startYear && endYear
                    ? `${startYear}/${endYear}`
                    : "—";

                const suggestedClassroom =
                  classrooms.find(
                    (classroom) =>
                      classroom.className ===
                        suggestedClassName &&
                      classroom.schoolYear?.id ===
                        currentYear?.id
                  );

                const schoolYearInfo = GetSchoolYear(
                  child.dateBirth,
                  classrooms,
                  schoolYears
                );

                const projectedYear =
                  typeof schoolYearInfo?.projectedYear ===
                  "string"
                    ? schoolYearInfo.projectedYear
                    : "—";

                const projectedYearIsEstimate =
                  projectedYear.startsWith("~") ||
                  Boolean(schoolYearInfo?.isEstimate);

                const isAssigned =
                  assignedChildren.includes(child.id);

                const translatedSuggestedClassName =
                  isTooYoung
                    ? t("waiting-list.too_young", {
                        defaultValue:
                          "Too young for this school year",
                      })
                    : translateClassName(
                        suggestedClassroom ||
                          suggestedClassName
                      ) ||
                      suggestedClassName ||
                      "—";

                const isDropdownOpen =
                  showDropdown === child.id;

                return (
                  <LdsTableRow
                    key={String(child.id)}
                    className={`relative ${
                      isAssigned
                        ? "cursor-default opacity-50"
                        : "cursor-pointer"
                    } ${isDropdownOpen ? "z-30 [&>td]:relative [&>td]:z-30" : ""}`}
                    onClick={() => {
                      if (!isAssigned) {
                        navigate(
                          `/family/children/${child.id}/details`
                        );
                      }
                    }}
                  >
                    <LdsTableCell>
                      <GenderIcon gender={child.gender} />
                    </LdsTableCell>

                    <LdsTableCell>
                      <div
                        className={
                          isAssigned ? "line-through" : ""
                        }
                      >
                        <span className="font-semibold text-slate-950 dark:text-white">
                          {[child.firstName, child.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </span>

                        <span className="ml-2 inline-flex items-center gap-1 align-middle">
                          <CommittedValidate
                            committed={child.committed}
                          />
                          <WaitingValidate
                            waiting={child.waiting}
                          />
                        </span>
                      </div>
                    </LdsTableCell>

                    <LdsTableCell className="text-slate-700 dark:text-slate-300">
                      {formatDate(child.dateBirth)}{" "}
                      <span className="font-semibold text-slate-950 dark:text-white">
                        ({calculateAge(child.dateBirth)})
                      </span>
                    </LdsTableCell>

                    <LdsTableCell className="text-slate-700 dark:text-slate-300">
                      {projectedYear}
                      {projectedYearIsEstimate ? (
                        <span className="ml-1 text-xs italic text-slate-500">
                          {t("common.estimated", {
                            defaultValue: "estimated",
                          })}
                        </span>
                      ) : null}
                    </LdsTableCell>

                    <LdsTableCell
                      className={`${
                        isTooYoung &&
                        !showFutureAssignments
                          ? "font-semibold text-rose-600 dark:text-rose-300"
                          : "text-slate-700 dark:text-slate-300"
                      }`}
                    >
                      {showFutureAssignments ? (
                        <div className="flex flex-wrap items-center gap-2">
                          {(
                            child.futureClassroomAssignments ??
                            []
                          ).map((assignment) => (
                            <span
                              key={`${child.id}-${String(
                                assignment.id ??
                                  assignment.className ??
                                  assignment.startDate
                              )}`}
                            >
                              {getFutureAssignmentLabel(
                                assignment
                              )}
                            </span>
                          ))}

                          <Badge tone="success" title={t("reports.unassigned.planned_badge",{ defaultValue: "Planned" })}>
                            {t(
                              "reports.unassigned.planned_badge",
                              { defaultValue: "Planned" }
                            )}
                          </Badge>
                        </div>
                      ) : (
                        <>
                          {translatedSuggestedClassName}{" "}
                          <span className="text-slate-500">
                            ({currentSchoolYearLabel})
                          </span>
                        </>
                      )}
                    </LdsTableCell>

                    {allowAssignmentActions ? (
                      <LdsTableCell
                        className={`relative text-right ${isDropdownOpen ? "z-50" : ""}`}
                        data-cu-dropdown={String(child.id)}
                      >
                        {!isAssigned ? (
                          <div className="relative inline-flex">
                            <button
                              type="button"
                              disabled={
                                !suggestedClassroom?.id
                              }
                              className="inline-flex items-center gap-2 rounded-l-full border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300 dark:disabled:border-slate-700 dark:disabled:bg-slate-700"
                              onClick={(
                                event: MouseEvent<HTMLButtonElement>
                              ) => {
                                event.preventDefault();
                                event.stopPropagation();

                                if (
                                  !suggestedClassroom
                                ) {
                                  return;
                                }

                                void handleAssign(
                                  child,
                                  suggestedClassroom
                                );
                              }}
                            >
                              <FaUserPlus aria-hidden="true" />
                              {t("waiting-list.assign", {
                                className:
                                  translatedSuggestedClassName,
                                defaultValue:
                                  "Assign to {{className}}",
                              })}
                            </button>

                            <button
                              type="button"
                              aria-label={t(
                                "waiting-list.more-options",
                                {
                                  defaultValue:
                                    "More assignment options",
                                }
                              )}
                              className="rounded-r-full border border-l-0 border-emerald-600 bg-emerald-600 px-3 text-white transition hover:bg-emerald-700"
                              onClick={(
                                event: MouseEvent<HTMLButtonElement>
                              ) => {
                                event.preventDefault();
                                event.stopPropagation();
                                const nextOpen = !isDropdownOpen;
                                if (nextOpen) {
                                  const rect = event.currentTarget.getBoundingClientRect();
                                  setDropdownPosition({
                                    top: rect.bottom + window.scrollY + 8,
                                    left: Math.max(8, rect.right + window.scrollX - 224),
                                  });
                                } else {
                                  setDropdownPosition(null);
                                }
                                setShowDropdown(
                                  nextOpen ? child.id : null
                                );
                              }}
                            >
                              <FaChevronDown
                                aria-hidden="true"
                              />
                            </button>

                            {isDropdownOpen && dropdownPosition && typeof document !== "undefined" ? createPortal(
                              <div
                                data-cu-dropdown={String(child.id)}
                                className="absolute z-[9999] w-56 rounded-2xl border border-slate-200 bg-white p-1.5 text-left shadow-xl dark:border-slate-700 dark:bg-slate-900"
                                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                              >
                                <button
                                  type="button"
                                  className="block w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                                  onClick={(
                                    event: MouseEvent<HTMLButtonElement>
                                  ) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setShowDropdown(null);
                                    setDropdownPosition(null);
                                    setSelectedChild(child);
                                    setShowModal(true);
                                  }}
                                >
                                  {t(
                                    "waiting-list.more-options",
                                    {
                                      defaultValue:
                                        "Choose another classroom",
                                    }
                                  )}
                                </button>
                              </div>,
                              document.body
                            ) : null}
                          </div>
                        ) : null}
                      </LdsTableCell>
                    ) : null}
                  </LdsTableRow>
                );
              })}
            </LdsTableBody>
          </LdsTable>
        </LdsTableScroll>
      </LdsTableCard>
    );
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <ErrorPage
        message={error.message}
        status={error.status}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <>
      <style>{`
        @keyframes lds-page-enter {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes lds-section-enter {
          from {
            opacity: 0;
            transform: translateY(18px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .lds-page-enter {
          animation: lds-page-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .lds-section-enter {
          animation: lds-section-enter 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        @media (prefers-reduced-motion: reduce) {
          .lds-page-enter,
          .lds-section-enter {
            animation: none;
          }
        }
      `}</style>

      <main className="lds-page-enter min-h-full px-4 py-5 sm:px-6 lg:px-8">
      {isMobileLayout ? (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-slate-950/95 px-6 text-center text-white">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-xl">
            <FaUsersSlash aria-hidden="true" />
          </div>

          <h2 className="mt-4 text-xl font-bold">
            {t(
              "waiting-list.mobile_not_supported_title",
              {
                defaultValue:
                  "Not available on mobile",
              }
            )}
          </h2>

          <p className="mt-2 max-w-sm text-sm leading-6 text-slate-300">
            {t(
              "waiting-list.mobile_not_supported_message",
              {
                defaultValue:
                  "This page requires a laptop or desktop to provide enough space for the planning tables.",
              }
            )}
          </p>

        </div>
      ) : null}

      <div className="mx-auto max-w-7xl rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
        <div className="space-y-5">
        <section className="lds-section-enter relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 items-center gap-5">
            <div className="flex min-w-0 flex-1 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-rose-200 to-amber-50 text-3xl text-rose-700 shadow-lg dark:border-slate-900 dark:from-rose-800 dark:to-amber-950 dark:text-rose-100 sm:h-24 sm:w-24">
                <FaTriangleExclamation aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("signals.overview.title", { defaultValue: "Signals" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  {t("reports.unassigned.unassigned_children", {
                    defaultValue: "Unassigned children",
                  })}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("reports.unassigned.explanation", {
                    defaultValue:
                      "Review children without a current classroom and assign them where appropriate.",
                  })}
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-200">
                    {t("reports.unassigned.total", {
                      count: children.length,
                      defaultValue: "{{count}} children",
                    })}
                  </span>
                  <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
                    {t("reports.unassigned.action_required_count", {
                      count: categorizedChildren.actionRequired.length,
                      defaultValue: "{{count}} require action",
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex"
            aria-hidden="true"
          >
            <FaTriangleExclamation className="text-[110px] text-rose-200/70 dark:text-rose-800/40" />
          </div>
        </section>

        <section className="lds-section-enter rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <label
            htmlFor="unassigned-search"
            className="mb-2 block text-xs font-bold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400"
          >
            {t("common.search", { defaultValue: "Search" })}
          </label>

          <div className="relative">
            <FaMagnifyingGlass
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              id="unassigned-search"
              type="search"
              placeholder={t(
                "waiting-list.search_placeholder",
                {
                  defaultValue: "Search by child name",
                }
              )}
              value={searchTerm}
              onChange={(
                event: ChangeEvent<HTMLInputElement>
              ) => setSearchTerm(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/10"
            />
          </div>
        </section>

        {searchFilteredChildren.length === 0 ? (
          <section className="rounded-[24px] border border-dashed border-slate-300 bg-white/75 p-10 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900/70">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-xl text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              <FaCalendarCheck aria-hidden="true" />
            </div>

            <h2 className="mt-4 text-xl font-bold text-slate-950 dark:text-white">
              {t(
                "reports.unassigned.no_results_title",
                { defaultValue: "No children found" }
              )}
            </h2>

            <p className="mx-auto mt-2 max-w-lg text-sm text-slate-600 dark:text-slate-300">
              {t(
                "reports.unassigned.no_unassigned_children",
                {
                  defaultValue:
                    "No unassigned children match the current search.",
                }
              )}
            </p>
          </section>
        ) : (
          <div className="lds-section-enter space-y-8">
            <section>
              {renderSectionHeader(
                t(
                  "reports.unassigned.action_required",
                  { defaultValue: "Action required" }
                ),
                t(
                  "reports.unassigned.action_required_description",
                  {
                    defaultValue:
                      "Children aged five or older who require immediate placement review.",
                  }
                ),
                categorizedChildren.actionRequired.length,
                "danger"
              )}

              {categorizedChildren.actionRequired.length >
              0 ? (
                renderChildrenTable(
                  categorizedChildren.actionRequired
                )
              ) : (
                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                  {t(
                    "reports.unassigned.no_action_required_children",
                    {
                      defaultValue:
                        "No children currently require immediate action.",
                    }
                  )}
                </p>
              )}
            </section>

            <section>
              {renderSectionHeader(
                t("reports.unassigned.may_attend", {
                  defaultValue: "May attend",
                }),
                t(
                  "reports.unassigned.may_attend_description",
                  {
                    defaultValue:
                      "Children aged four who may be eligible for classroom placement.",
                  }
                ),
                categorizedChildren.mayAttend.length,
                "warning"
              )}

              {categorizedChildren.mayAttend.length >
              0 ? (
                renderChildrenTable(
                  categorizedChildren.mayAttend
                )
              ) : (
                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                  {t(
                    "reports.unassigned.no_may_attend_children",
                    {
                      defaultValue:
                        "No children currently fall into this category.",
                    }
                  )}
                </p>
              )}
            </section>

            <section>
              {renderSectionHeader(
                t(
                  "reports.unassigned.planned_future_school_year",
                  {
                    defaultValue:
                      "Planned for a future school year",
                  }
                ),
                t(
                  "reports.unassigned.future_planned_description",
                  {
                    defaultValue:
                      "Children who already have a future classroom assignment.",
                  }
                ),
                categorizedChildren.futurePlanned.length,
                "success"
              )}

              {categorizedChildren.futurePlanned.length >
              0 ? (
                renderChildrenTable(
                  categorizedChildren.futurePlanned,
                  { showFutureAssignments: true }
                )
              ) : (
                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                  {t(
                    "reports.unassigned.no_future_planned_children",
                    {
                      defaultValue:
                        "No children currently have a future classroom assignment.",
                    }
                  )}
                </p>
              )}
            </section>

            <section>
              {renderSectionHeader(
                t("reports.unassigned.not_assigned", {
                  defaultValue: "Not assigned",
                }),
                t(
                  "reports.unassigned.not_assigned_description",
                  {
                    defaultValue:
                      "Children who are not yet eligible and have no future classroom assignment.",
                  }
                ),
                categorizedChildren.unassigned.length,
                "neutral"
              )}

              {categorizedChildren.unassigned.length >
              0 ? (
                renderChildrenTable(
                  categorizedChildren.unassigned
                )
              ) : (
                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                  {t(
                    "reports.unassigned.no_not_assigned_children",
                    {
                      defaultValue:
                        "No children currently fall into this category.",
                    }
                  )}
                </p>
              )}
            </section>
          </div>
        )}
      </div>

        </div>

      {showModal && selectedChild && typeof document !== "undefined" ? createPortal(
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/70 p-4 backdrop-blur-sm"
        >
          <div className="w-full max-w-xl rounded-3xl border border-white/60 bg-white p-6 shadow-[0_35px_70px_rgba(0,0,0,0.45)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-indigo-600 dark:text-indigo-300">
                  {[
                    selectedChild.firstName,
                    selectedChild.lastName,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                  {t(
                    "waiting-list.select-modal.select-class",
                    { defaultValue: "Select classroom" }
                  )}
                </h2>
              </div>

              <button
                type="button"
                aria-label={t("common.close", {
                  defaultValue: "Close",
                })}
                onClick={() => {
                  setShowModal(false);
                  setSelectedChild(null);
                  setClassroomSearchTerm("");
                }}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <FaXmark aria-hidden="true" />
              </button>
            </div>

            <div className="mt-5">
              <SchoolYearDropdown
                schoolYears={schoolYears
                  .filter((schoolYear) => schoolYear.id != null)
                  .map((schoolYear) => ({
                    id: String(schoolYear.id),
                    startDate: Array.isArray(schoolYear.startDate) || typeof schoolYear.startDate === "string"
                      ? schoolYear.startDate
                      : undefined,
                    endDate: Array.isArray(schoolYear.endDate) || typeof schoolYear.endDate === "string"
                      ? schoolYear.endDate
                      : undefined,
                  }))}
                selectedSchoolYearId={modalSchoolYearId}
                onChange={(schoolYearId) => {
                  setModalSchoolYearId(schoolYearId);
                  setClassroomSearchTerm("");
                }}
                label={t("waiting-list.select-modal.school-year", {
                  defaultValue: "School year",
                })}
              />
            </div>

            <div className="relative mt-5">
              <FaMagnifyingGlass
                aria-hidden="true"
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="search"
                autoFocus
                placeholder={t(
                  "waiting-list.select-modal.search-placeholder",
                  {
                    defaultValue: "Search classrooms",
                  }
                )}
                value={classroomSearchTerm}
                onChange={(
                  event: ChangeEvent<HTMLInputElement>
                ) =>
                  setClassroomSearchTerm(
                    event.target.value
                  )
                }
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-950 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-950"
              />
            </div>

            <div className="mt-3 max-h-72 space-y-2 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-950">
              {modalClassrooms
                .filter((classroom) =>
                  classroom.className
                    .toLowerCase()
                    .includes(
                      classroomSearchTerm.toLowerCase()
                    )
                )
                .map((classroom) => {
                  const schoolYear =
                    formatSchoolYearRange(
                      classroom.startDate,
                      classroom.endDate
                    );

                  const translatedClassName =
                    translateClassName(classroom) ||
                    classroom.className;

                  return (
                    <div
                      key={String(classroom.id)}
                      className="flex items-center justify-between gap-4 rounded-xl bg-white px-4 py-3 shadow-sm dark:bg-slate-900"
                    >
                      <div>
                        <p className="font-semibold text-slate-950 dark:text-white">
                          {translatedClassName}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {schoolYear}
                        </p>
                      </div>

                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                        onClick={(event: MouseEvent<HTMLButtonElement>) => {
                          event.preventDefault();
                          event.stopPropagation();
                          void handleAssign(
                            selectedChild,
                            classroom
                          )
                        }}
                      >
                        {t(
                          "waiting-list.select-modal.select",
                          { defaultValue: "Select" }
                        )}
                        <FaArrowRight
                          aria-hidden="true"
                          className="text-xs"
                        />
                      </button>
                    </div>
                  );
                })}
              {modalClassrooms.filter((classroom) =>
                classroom.className.toLowerCase().includes(classroomSearchTerm.toLowerCase())
              ).length === 0 ? (
                <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-300">
                  {t("waiting-list.select-modal.no-classrooms", {
                    defaultValue: "No classrooms found for the current school year.",
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      </main>
    </>
  );
}
